import {useEffect, useRef, useState} from "react";
import {PhoneHQButton, PhoneHQGlyph, PhoneHQDropdown, PhoneHQInput} from 'ch3-ui-lib';
import AudioPlayer from "../../../components/AudioPlayer/AudioPlayer";
import {usePreviewEnsTtsMutation} from "../../../store/features/ens/ensService";
import {
    ENS_TTS_GENDERS,
    ENS_TTS_LANGUAGES,
    ENS_TTS_MAX_TEXT_LENGTH,
    EnsTtsGender,
    EnsTtsLanguage,
    EnsTtsRequest,
    EnsVoicePrompt,
} from "../../../store/features/ens/types";
import {EnsTextareaField} from "./EnsTextareaField";

interface Props {
    value: EnsVoicePrompt | null;
    draft: EnsTtsRequest;
    autoPreview?: boolean;
    onChange: (value: EnsVoicePrompt | null) => void;
    onDraftChange: (value: EnsTtsRequest) => void;
}

const LANGUAGE_LABEL: Record<EnsTtsLanguage, string> = {
    ENGLISH: 'English',
    POLISH: 'Polish',
    CZECH: 'Czech',
    GERMAN: 'German',
    SPANISH: 'Spanish',
    CHINESE: 'Chinese',
    FRENCH: 'French',
    ITALIAN: 'Italian',
    JAPANESE: 'Japanese',
};

const GENDER_LABEL: Record<EnsTtsGender, string> = {
    UNSPECIFIED: 'Unspecified',
    MALE: 'Male',
    FEMALE: 'Female',
    NEUTRAL: 'Neutral',
};

const SECONDARY_TEXT_BUTTON_CLASS =
    '!h-8 !rounded-lg !px-3 !py-0 !text-ink-secondary hover:!text-brand-ink disabled:!text-ink-disabled';
const PRIMARY_BUTTON_CLASS =
    '!h-8 !rounded-lg !border-brand !px-4 !py-0 shadow-sm disabled:!border-line disabled:!bg-surface-muted disabled:!text-ink-disabled';

const renderDropdownTrigger = (label: string) => (
    <div
        role="button"
        tabIndex={0}
        className="w-full cursor-pointer"
    >
        <PhoneHQInput
            readOnly
            tabIndex={-1}
            value={label}
            aria-hidden
            className="text-sm"
            suffixIcon={<PhoneHQGlyph name="keyboard_arrow_down" size={20} className="text-ink-muted"/>}
        />
    </div>
);

export const EnsTtsPromptInput = ({value, draft, autoPreview = false, onChange, onDraftChange}: Props) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const [previewTts, {isLoading: isPreviewing}] = usePreviewEnsTtsMutation();

    const previewUrlRef = useRef<string | null>(null);
    useEffect(() => {
        previewUrlRef.current = previewUrl;
    }, [previewUrl]);
    useEffect(() => () => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    }, []);
    const trimmed = draft.text.trim();
    const tooLong = draft.text.length > ENS_TTS_MAX_TEXT_LENGTH;
    const canPreview = trimmed.length > 0 && !tooLong && !isPreviewing;

    useEffect(() => {
        if (!autoPreview || value == null || trimmed.length === 0 || tooLong) return;

        let cancelled = false;
        const loadPreview = async () => {
            try {
                const preview = await previewTts({...draft, text: trimmed}).unwrap();
                if (cancelled) {
                    URL.revokeObjectURL(preview.url);
                    return;
                }
                setPreviewUrl((previous) => {
                    if (previous) URL.revokeObjectURL(previous);
                    return preview.url;
                });
            } catch {
                // toast handled in service
            }
        };

        void loadPreview();
        return () => {
            cancelled = true;
        };
    }, [autoPreview, draft, previewTts, tooLong, trimmed, value]);

    const replacePreviewUrl = (next: string | null) => {
        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return next;
        });
    };

    const handleDraftChange = (nextDraft: EnsTtsRequest) => {
        replacePreviewUrl(null);
        onDraftChange(nextDraft);
    };

    const handlePreview = async () => {
        if (!canPreview) return;
        const params = {...draft, text: trimmed};
        try {
            const preview = await previewTts(params).unwrap();
            replacePreviewUrl(preview.url);
        } catch {
            // toasts handled in service
        }
    };

    const handleEdit = () => {
        onChange(null);
        replacePreviewUrl(null);
    };

    if (value != null) {
        return (
            <div className="flex flex-col gap-2.5">

                {previewUrl ? (
                    <AudioPlayer src={previewUrl}/>
                ) : isPreviewing && autoPreview ? (
                    <div className="text-xs text-ink-muted">Loading voice preview…</div>
                ) : (
                    <div className="text-xs text-ink-muted">Voice prompt ready.</div>
                )}

                <div className="flex w-full justify-end gap-2">
                    <PhoneHQButton variant="secondary-contrast" size="sm" disabled={isPreviewing} onClick={handleEdit} className={SECONDARY_TEXT_BUTTON_CLASS} phq-id="ensTts-deleteBtn"><PhoneHQGlyph name="edit" size={16}/>Edit voice prompt</PhoneHQButton>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5">
            <EnsTextareaField
                phqId="ensTts-textInput"
                label="Text"
                placeholder="What the recipient will hear during the phone call"
                value={draft.text}
                maxLength={ENS_TTS_MAX_TEXT_LENGTH}
                error={tooLong ? 'Text is too long' : null}
                minHeightClassName="min-h-[62px]"
                labelClassName="text-sm font-medium text-ink"
                onChange={(text) => handleDraftChange({...draft, text})}
            />

            <div className="grid gap-2 sm:grid-cols-2">
                <div className="flex-1">
                    <div className="mb-1 text-sm font-medium">Language</div>
                    <PhoneHQDropdown
                        align="start"
                        sideOffset={4}
                        trigger={renderDropdownTrigger(LANGUAGE_LABEL[draft.language])}
                    >
                        {ENS_TTS_LANGUAGES.map((l) => (
                            <PhoneHQDropdown.Item key={l} onSelect={() => handleDraftChange({...draft, language: l})}>
                                <div className="flex w-[200px] items-center justify-between gap-3">
                                    <span>{LANGUAGE_LABEL[l]}</span>
                                    {l === draft.language && <PhoneHQGlyph name="check" size={16}/>}
                                </div>
                            </PhoneHQDropdown.Item>
                        ))}
                    </PhoneHQDropdown>
                </div>
                <div className="flex-1">
                    <div className="mb-1 text-sm font-medium">Voice</div>
                    <PhoneHQDropdown
                        align="start"
                        sideOffset={4}
                        trigger={renderDropdownTrigger(GENDER_LABEL[draft.gender])}
                    >
                        {ENS_TTS_GENDERS.map((g) => (
                            <PhoneHQDropdown.Item key={g} onSelect={() => handleDraftChange({...draft, gender: g})}>
                                <div className="flex w-[200px] items-center justify-between gap-3">
                                    <span>{GENDER_LABEL[g]}</span>
                                    {g === draft.gender && <PhoneHQGlyph name="check" size={16}/>}
                                </div>
                            </PhoneHQDropdown.Item>
                        ))}
                    </PhoneHQDropdown>
                </div>
            </div>

            {previewUrl && <AudioPlayer src={previewUrl}/>}

            <div className="flex w-full justify-end gap-2">
                <PhoneHQButton size="sm" disabled={!canPreview} loading={isPreviewing} onClick={handlePreview} className={PRIMARY_BUTTON_CLASS} phq-id="ensTts-previewBtn"><PhoneHQGlyph name="graphic_eq" size={16}/>Preview voice</PhoneHQButton>
            </div>
        </div>
    );
};
